import React from "react";
import { X } from "lucide-react";
import { FlightSuggestion } from "apis/loadsServices";

interface AiSuggestiveFiltersProps {
  suggestions?: FlightSuggestion[];
  appliedSuggestionId?: string | null;
  onSelectSuggestion?: (suggestion: FlightSuggestion) => void;
  onDismissSuggestion?: () => void;
  // Legacy handler fallback if needed
  onApplySuggestion?: (sortBy: string | null, sortOrder: string | null) => void;
  sortBy?: string | null;
  sortOrder?: string | null;
  hasConnections?: boolean;
  hasDirect?: boolean;
  isLoading?: boolean;
}

export const AiSuggestiveFilters: React.FC<AiSuggestiveFiltersProps> = ({
  suggestions = [],
  appliedSuggestionId = null,
  onSelectSuggestion,
  onDismissSuggestion,
  onApplySuggestion,
  sortBy,
  sortOrder,
  hasConnections = false,
  hasDirect = true,
  isLoading = false,
}) => {
  // Filter chips based on relevance to current result mix
  const visibleSuggestions = (suggestions || []).filter((chip) => {
    if (chip.appliesTo === "connections") return hasConnections;
    if (chip.appliesTo === "direct") return hasDirect;
    return true; // "both" or fallback
  });

  // An empty array is a normal, valid response — render no chip strip, not an empty state
  if (!visibleSuggestions || visibleSuggestions.length === 0) {
    return null;
  }

  return (
    <div className="bg-white border border-[#FFC82C] rounded-b-2xl rounded-t-lg pt-5 pb-3.5 px-3.5 sm:px-5 sm:pt-5 sm:pb-4 transition-all shadow-none">
      <div className="flex items-center gap-3 sm:gap-4">
        {/* Left side: 3D Robot Mascot Icon */}
        <div className="relative flex items-center justify-center shrink-0 translate-y-1 mt-0.5">
          <img
            src="/ai-robot-mascot.png"
            alt="AI Robot Mascot"
            className="w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 object-contain shrink-0"
          />
        </div>

        {/* Right side: AI Assist Heading + Chips Row */}
        <div className="flex flex-col gap-1 flex-1 min-w-0 justify-center">
          <span className="text-black text-sm sm:text-base font-bold font-inter tracking-tight leading-tight">
            AI Assist
          </span>

          <div className="flex items-center gap-2 sm:gap-2.5 overflow-x-auto no-scrollbar py-0.5 flex-nowrap">
            {visibleSuggestions.map((chip) => {
              const isActive = appliedSuggestionId === chip.id;

              return (
                <button
                  key={chip.id}
                  type="button"
                  disabled={isLoading}
                  onClick={() => {
                    if (isActive) {
                      if (onDismissSuggestion) {
                        onDismissSuggestion();
                      } else if (onApplySuggestion) {
                        onApplySuggestion(null, null);
                      }
                    } else {
                      if (onSelectSuggestion) {
                        onSelectSuggestion(chip);
                      } else if (onApplySuggestion) {
                        onApplySuggestion(
                          chip.directive?.sortBy || null,
                          chip.directive?.sortDirection || null
                        );
                      }
                    }
                  }}
                  className={`group flex items-center gap-1.5 px-3.5 py-1.5 sm:px-4 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium font-inter transition-all duration-200 cursor-pointer select-none whitespace-nowrap shrink-0 ${
                    isActive
                      ? "bg-gray-100 text-black border border-gray-300 shadow-sm font-semibold"
                      : "bg-gray-50 hover:bg-gray-100 text-black border border-gray-200 shadow-none"
                  } ${isLoading ? "opacity-75 cursor-wait" : ""}`}
                >
                  {/* 4-Point Star Icon */}
                  <svg
                    className="w-3.5 h-3.5 shrink-0 text-black fill-black"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" />
                  </svg>
                  <span>{chip.label}</span>
                  {isActive && (
                    <span
                      className="ml-0.5 p-0.5 rounded-full hover:bg-black/10 transition-colors"
                      title="Clear filter"
                      aria-label="Clear filter"
                    >
                      <X className="w-3.5 h-3.5 text-black stroke-[3]" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiSuggestiveFilters;


